// GUIDE_007 — Automation meeting-summary spec. Bespoke layout (DocSpec
// cards + step flow + principles + triggers + notes). Visual preserved;
// content sourced from page_content row at slug
// `guide-007-automation-meeting-summary-guide`.

import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useApi } from "@/lib/api";
import {
  ColorBlock,
  StepFlow,
  type ColorBlockTint,
} from "@/components/page";

type Size = "sm" | "md" | "lg";

type DocSpec = {
  emoji: string;
  number: string;
  title: string;
  tint: ColorBlockTint;
  contains: string[];
  filename: string;
  location: string;
  example: string;
};

type AutomationSpec = {
  kind: "automation-spec-v1";
  header: { eyebrow?: string; title: string; lede?: string };
  documentsEyebrow?: string;
  documents: DocSpec[];
  stepsEyebrow?: string;
  steps: { label: string; body: string }[];
  principles: {
    tint: ColorBlockTint;
    size?: Size;
    eyebrow?: string;
    items: { num: string; title: string; body: string }[];
  };
  triggers: {
    tint: ColorBlockTint;
    size?: Size;
    eyebrow?: string;
    intro?: string;
    phrases: string[];
  };
  notes: { label: string; body: string }[];
};

const SLUG = "guide-007-automation-meeting-summary-guide";

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div
      dir="rtl"
      className="min-h-svh -mx-6 -my-8 px-6 py-6 sm:py-8 bg-stone-50"
    >
      <div className="mx-auto max-w-5xl space-y-10">{children}</div>
    </div>
  );
}

function DocumentCard({ doc }: { doc: DocSpec }) {
  return (
    <ColorBlock tint={doc.tint} size="md">
      <div className="flex items-start justify-between gap-3 mb-3">
        <span
          className="text-[11px] font-semibold tracking-[0.22em] tabular-nums opacity-70"
          dir="ltr"
        >
          {doc.number}
        </span>
        <span className="text-2xl" aria-hidden>
          {doc.emoji}
        </span>
      </div>
      <h3 className="text-xl sm:text-2xl font-bold tracking-tight text-stone-900 leading-tight mb-3">
        {doc.title}
      </h3>
      <ul className="space-y-1.5 mb-4">
        {doc.contains.map((c, i) => (
          <li
            key={i}
            className="text-sm text-stone-700/85 leading-snug ps-4 relative before:content-['—'] before:absolute before:start-0 before:text-stone-500"
          >
            {c}
          </li>
        ))}
      </ul>
      <div className="space-y-2 pt-3 border-t border-stone-900/10">
        <FieldRow label="קובץ" value={doc.filename} />
        <FieldRow label="מיקום" value={doc.location} />
        <FieldRow label="דוגמה" value={doc.example} />
      </div>
    </ColorBlock>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[5rem_1fr] gap-3 text-xs">
      <span className="font-semibold uppercase tracking-[0.18em] text-stone-700/65">
        {label}
      </span>
      <span
        className="font-mono text-stone-800 leading-snug break-all text-[11.5px]"
        dir="ltr"
      >
        {value}
      </span>
    </div>
  );
}

function NoteCard({ label, body }: { label: string; body: string }) {
  return (
    <div className="rounded-2xl bg-white px-5 py-4">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-stone-500 mb-2">
        {label}
      </p>
      <p className="text-sm text-stone-700 leading-relaxed">{body}</p>
    </div>
  );
}

export function Guide007AutomationMeetingSummaryGuide() {
  const api = useApi();

  const { data, isLoading, error } = useQuery({
    queryKey: ["kb", SLUG],
    queryFn: async (): Promise<AutomationSpec> => {
      const res = await api(`/api/kb/${SLUG}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { content: AutomationSpec };
      return json.content;
    },
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <PageFrame>
        <div className="flex items-center gap-2 p-2 text-sm text-stone-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          טוען…
        </div>
      </PageFrame>
    );
  }

  if (error || !data) {
    return (
      <PageFrame>
        <div className="rounded-2xl border border-rose-300/60 bg-rose-50/40 p-5">
          <div className="text-sm font-semibold text-rose-700">לא ניתן לטעון את המדריך</div>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      {/* Hero */}
      <header>
        {data.header.eyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-stone-500">
            {data.header.eyebrow}
          </p>
        )}
        <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-[-0.02em] leading-tight text-stone-900">
          {data.header.title}
        </h1>
        {data.header.lede && (
          <p className="mt-3 max-w-2xl text-base sm:text-lg text-stone-600 leading-snug">
            {data.header.lede}
          </p>
        )}
      </header>

      {/* The 4 documents — 2x2 grid */}
      <section>
        {data.documentsEyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500 mb-4">
            {data.documentsEyebrow}
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {data.documents.map((doc) => (
            <DocumentCard key={doc.number} doc={doc} />
          ))}
        </div>
      </section>

      {/* Workflow */}
      <section>
        {data.stepsEyebrow && (
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-stone-500 mb-4">
            {data.stepsEyebrow}
          </p>
        )}
        <StepFlow items={data.steps} />
      </section>

      {/* Principles + triggers */}
      <section className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
        <ColorBlock
          tint={data.principles.tint}
          size={data.principles.size ?? "md"}
          eyebrow={data.principles.eyebrow}
        >
          <ul className="space-y-4">
            {data.principles.items.map((p) => (
              <li key={p.num} className="grid grid-cols-[2.5rem_1fr] gap-3">
                <span
                  className="text-[11px] font-semibold tracking-[0.18em] tabular-nums text-stone-700/70 mt-1"
                  dir="ltr"
                >
                  {p.num}
                </span>
                <div>
                  <p className="text-base font-semibold text-stone-900 tracking-tight leading-tight">
                    {p.title}
                  </p>
                  <p className="mt-1 text-sm text-stone-700/85 leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </ColorBlock>

        <ColorBlock
          tint={data.triggers.tint}
          size={data.triggers.size ?? "md"}
          eyebrow={data.triggers.eyebrow}
        >
          {data.triggers.intro && (
            <p className="text-sm text-stone-700/85 leading-relaxed mb-3">
              {data.triggers.intro}
            </p>
          )}
          <ul className="space-y-2">
            {data.triggers.phrases.map((phrase, idx) => (
              <li
                key={idx}
                className="rounded-xl bg-white/65 px-4 py-2.5 text-sm text-stone-800 leading-snug"
              >
                "{phrase}"
              </li>
            ))}
          </ul>
        </ColorBlock>
      </section>

      {/* Notes */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {data.notes.map((n, i) => (
          <NoteCard key={i} label={n.label} body={n.body} />
        ))}
      </section>
    </PageFrame>
  );
}

(Guide007AutomationMeetingSummaryGuide as unknown as { path: string }).path =
  "/automations/automation-meeting-summary-guide";
